"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function () {
  const stages = [{
    number: "01",
    label: "— DISCOVERY",
    title: "We begin / by listening.",
    description: "Every hotel has a unique story and untapped potential. We spend time analyzing your current performance, market position, and operational bottlenecks to identify immediate growth opportunities.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_discovery_analysis.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_discovery_analysis.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_discovery_analysis.png-wvc-sizes",
    alt: "Revenue manager reviewing hotel performance data on a laptop",
    side: "right"
  }, {
    number: "02",
    label: "— ANALYSIS",
    title: "Then we look / closely.",
    description: "Using advanced data analytics and OTA benchmarking, we uncover hidden revenue leaks. Precision in data leads to precision in pricing and distribution strategy.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_data_insights.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_data_insights.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_data_insights.webp-wvc-sizes",
    alt: "Digital dashboard showing hotel revenue growth charts",
    side: "left"
  }, {
    number: "03",
    label: "— CURATION",
    title: "We compose / a plan.",
    description: "No two properties are alike. We craft a bespoke revenue management roadmap, integrating the right tech stack and OTA strategies tailored to your specific market demand.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_strategy_planning.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_strategy_planning.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_strategy_planning.png-wvc-sizes",
    alt: "Strategic roadmap document for hotel revenue optimization",
    side: "right"
  }, {
    number: "04",
    label: "— VITALITY",
    title: "And then / you grow.",
    description: "We partner with you long-term. Through continuous monitoring, dynamic rate adjustments, and proactive OTA management, we ensure your revenue keeps climbing.",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_revenue_growth.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_revenue_growth.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hotel_revenue_growth.png-wvc-sizes",
    alt: "Hotel lobby interior reflecting luxury and guest satisfaction",
    side: "left"
  }];
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.2
  });
  return <section data-section-id="4583" id="strategicimplementationplan" className="bg-background text-foreground py-40 overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {}
        <div ref={header.ref} className={`grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12 mb-32 transition-all duration-1000 ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <div className="lg:col-span-7">
            <p className="font-default text-xs tracking-widest text-muted-foreground mb-8 uppercase">
              — Our Methodology
            </p>
            <h2 className="font-serif font-default text-foreground leading-[0.95] tracking-tight" style={{
            fontSize: "clamp(3rem, 7vw, 6rem)"
          }}>
              Revenue strategy /{" "}
              <span className="italic text-secondary">refined.</span>
            </h2>
          </div>
          <div className="lg:col-span-4 lg:col-start-9 flex items-end">
            <p className="font-serif italic text-lg lg:text-xl text-muted-foreground leading-relaxed">
              At HotelRevBaBa, revenue management is a science of precision. Four strategic phases designed to maximize your property's profitability and market share.
            </p>
          </div>
        </div>

        {}
        <div className="relative">
          {}
          <ProgressiveSpine />

          <div className="relative space-y-20 lg:space-y-32">
            {stages.map((stage, i) => <Stage key={i} stage={stage} data-index={i} />)}
          </div>
        </div>
      </div>
    </section>;
}
function ProgressiveSpine() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.05,
    once: true
  });
  return <div ref={ref} className="absolute left-4 sm:left-8 lg:left-1/3 top-0 bottom-0 w-px pointer-events-none" aria-hidden="true">
      <div className="relative h-full w-px bg-border/40">
        <div className="absolute top-0 left-0 w-px bg-secondary/70 transition-all ease-out" style={{
        height: isVisible ? "100%" : "0%",
        transitionDuration: "2500ms"
      }} />
      </div>
    </div>;
}
type StageProps = {
  stage: {
    number: string;
    label: string;
    title: string;
    description: string;
    image: string;
    srcset: string;
    sizes: string;
    alt: string;
    side: string;
  };
  "data-index"?: number;
};
function Stage({
  stage,
  ...rest
}: StageProps) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.25
  });
  const isRight = stage.side === "right";
  return <div ref={ref} {...rest} className="relative grid grid-cols-12 gap-4 sm:gap-6 lg:gap-8 items-start">
      {}
      <div className={`absolute left-4 sm:left-8 lg:left-1/3 top-0 -translate-x-1/2 flex flex-col items-center transition-opacity duration-1000 ${isVisible ? "opacity-100" : "opacity-0"}`}>
        <span className={`block size-2 rounded-lg transition-all duration-1000 ${isVisible ? "bg-secondary scale-100 shadow-md" : "bg-border scale-75"}`} />
        <span className="font-serif font-default text-foreground/80 text-xs sm:text-sm mt-3 -ml-0">
          {stage.number}
        </span>
      </div>

      {}
      {isRight ? <>
          {}
          <div className="hidden lg:block lg:col-span-4" />
          {}
          <div className="col-span-12 pl-12 sm:pl-16 lg:pl-0 lg:col-span-8">
            <StageContent stage={stage} isVisible={isVisible} alignImage="right" />
          </div>
        </> : <>
          {}
          <div className="col-span-12 pl-12 sm:pl-16 lg:pl-12 lg:col-span-7">
            <StageContent stage={stage} isVisible={isVisible} alignImage="left" />
          </div>
          {}
          <div className="hidden lg:block lg:col-span-5" />
        </>}
    </div>;
}
function StageContent({
  stage,
  isVisible,
  alignImage
}: {
  stage: {
    number: string;
    label: string;
    title: string;
    description: string;
    image: string;
    srcset: string;
    sizes: string;
    alt: string;
  };
  isVisible: boolean;
  alignImage: string;
}) {
  return <div className={`flex flex-col gap-8 transition-all duration-1000 ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
      <div className={`flex flex-col lg:flex-row gap-8 lg:gap-12 ${alignImage === "left" ? "lg:flex-row-reverse" : ""}`}>
        {}
        <div className="flex-1 max-w-xl">
          <p className="font-default text-xs tracking-widest text-secondary/80 mb-6 uppercase">
            {stage.label}
          </p>
          <h3 className="font-serif font-default text-foreground leading-[1.05] tracking-tight mb-6" style={{
          fontSize: "clamp(2.5rem, 5vw, 4rem)"
        }}>
            {stage.title.split("/").map((part, idx) => <span key={idx} className="block">
                {part.trim()}
                {idx === 0 && stage.title.includes("/") && <span className="text-secondary/40"> /</span>}
              </span>)}
          </h3>
          <p className="font-serif italic text-base sm:text-lg text-muted-foreground leading-relaxed max-w-md">
            {stage.description}
          </p>
        </div>

        {}
        <div className={`shrink-0 ${alignImage === "left" ? "lg:-translate-x-4 lg:-translate-y-4" : "lg:translate-x-4 lg:translate-y-8"}`}>
          <div className="overflow-hidden rounded-lg shadow-lg w-44 h-56 sm:w-56 sm:h-72">
            <img src={stage.image} data-wvc-srcset={stage.srcset} data-wvc-sizes={stage.sizes} alt={stage.alt} loading="lazy" decoding="async" className={`w-full h-full object-cover transition-transform duration-[2000ms] ${isVisible ? "scale-100" : "scale-110"}`} />
          </div>
        </div>
      </div>
    </div>;
}